<script setup lang="ts">
import {computed, defineAsyncComponent, onMounted, onUnmounted, ref, watch} from 'vue'
import {MapsData} from '@/interfaces/MapsViewInterface.ts'
import mapMarkerImgUrl from '../assets/MapMarker.png'
import {getTranslation, setShop, loadGoogleMaps} from '@/ts/utilities.ts'


const DealerCard = defineAsyncComponent(() => import('@/components/DealerCard.vue'))
const SelectElement = defineAsyncComponent(() => import('@/components/ui/form/SelectElement.vue'))

interface Props {
	mapsData: MapsData
}

declare let Cookiebot: any

const props = defineProps<Props>()

const mapLoaded = ref(false)
const selectedCountry = ref('')

type CountryApiItem = {
	Code2?: string,
	Name?: string,
}

type CountryOption = {
	id: string,
	name: string,
	value: string,
}

const countryOptions = ref<CountryOption[]>([])

const selectedCountryOptions = computed(() => {
	return countryOptions.value.filter((option) => option.value === selectedCountry.value)
})

type ShopInformationRow = {
	columnOne?: string,
	columnTwo?: string,
}

type ShopMarkerData = {
	ShopName: string,
	Address: string,
	PostalAndCity: string,
	MenuText: string,
	OpeningHours: string,
	Phone: string,
	Email: string,
	Link: string,
	Lat: number,
	Lng: number,
	SearchKeyWords: string,
	SetShopParameter: string,
	IsLocalWebshop: boolean,
	LocalWebshopLink: string,
	ShopInformationText?: string,
	ShopInformationList?: ShopInformationRow[],
}

const mapsData: MapsData = {
	apiKey: props.mapsData.apiKey,
	endpoint: '/mobler-api/v2/shops/getshops',
}

const mapContainer = ref<HTMLElement | null>(null)
let map: google.maps.Map | null = null
let dealers = ref<ShopMarkerData[]>([])
let infoWindows: google.maps.InfoWindow[] = []
let markers: google.maps.marker.AdvancedMarkerElement[] = []
let bodyClickListenerAttached = false
let removeChildPatched = false
let requestToken = 0
const mapCenter: google.maps.LatLngLiteral = { lat: 55.977675, lng: 10.933507 }

const viewShopText = getTranslation('FindShopCTA')
const openingHourstext = getTranslation('FindShopOpeningHoursHeader')

const hasCookieConsent = () => {
	return Boolean(Cookiebot?.consent?.marketing && Cookiebot?.consent?.statistics)
}

const patchGoogleMapsRemoveChild = () => {
	if (removeChildPatched) {
		return
	}

	const removeChild = Node.prototype.removeChild
	Node.prototype.removeChild = function <T extends Node>(child: T): T {
		if (child.parentNode !== this) {
			return child
		}

		return removeChild.call(this, child) as T
	}
	removeChildPatched = true
}

const buildEndpoint = (country: string) => {
	const endpoint = new URL(mapsData.endpoint, window.location.origin)
	endpoint.searchParams.set('countrycode', country)
	return endpoint.toString()
}

const handleCountryChange = (_filterType: string, selectedOptions: Array<{ value?: string }>) => {
	const country = selectedOptions[0]?.value

	if (!country || country === selectedCountry.value) {
		return
	}

	selectedCountry.value = country
}

const closeInfoWindows = () => {
	infoWindows.forEach((infoWindow) => infoWindow.close())
	infoWindows = []
}

const clearMarkers = () => {
	markers.forEach((marker) => {
		google.maps.event.clearInstanceListeners(marker)
		marker.map = null
	})
	markers = []
}

const handleBodyClick = (event: Event) => {
	const target = event.target as Element | null
	const link = target?.closest('.js-shop-link') as HTMLAnchorElement | null

	if (!link) {
		return
	}

	event.preventDefault()
	const dealer = dealers.value.find((item) => item.MenuText === link.dataset.dealer)
	if (!dealer) {
		return
	}

	const listElm = document.querySelector(`[data-dealer-card="${link.dataset.dealer}"]`)
	if (listElm !== null) {
		const button = listElm.querySelector('button[type="button"]')
		if (button !== null) {
			;(button as HTMLButtonElement).click()
			return
		}
	}

	setShop(dealer, false)
}

const ensureBodyClickHandler = () => {
	if (bodyClickListenerAttached) {
		return
	}

	document.body.addEventListener('click', handleBodyClick)
	bodyClickListenerAttached = true
}

const renderMap = async () => {
	if (!mapContainer.value) {
		return
	}

	patchGoogleMapsRemoveChild()

	const [{ Map, InfoWindow }, { AdvancedMarkerElement }] = await Promise.all([
		google.maps.importLibrary('maps') as Promise<google.maps.MapsLibrary>,
		google.maps.importLibrary('marker') as Promise<google.maps.MarkerLibrary>,
	])

	if (!map) {
		const zoom = dealers.value.length > 3 ? 7 : 15
		map = new Map(mapContainer.value, {
			center: mapCenter,
			zoom,
			disableDefaultUI: true,
			mapId: 'FindShop',
			gestureHandling: 'greedy'
		})

		const mapInstance = map
		if (mapInstance) {
			google.maps.event.addListenerOnce(mapInstance, 'idle', () => {
				google.maps.event.trigger(mapInstance, 'resize')
			})
		}
	}

	closeInfoWindows()
	clearMarkers()

	const markersData: ShopMarkerData[] = dealers.value
	markersData.forEach((dealer: ShopMarkerData) => {
		const shopInformationList = dealer.ShopInformationList ?? []
		const contentString = `
				<div class="flex flex-col gap-y-4 p-2 min-w-60 text-sm">
					<div class="p-4 absolute -right-6 -top-6 bg-white rounded-full shadow cursor-pointer w-12 h-12 flex justify-center items-center">
						<i class="fal fa-times text-black text-lg"></i>
					</div>
					<address class="not-italic">
						<p class="text-lg">${dealer.ShopName}</p>
						${dealer.Address}<br>
						${dealer.PostalAndCity}<br>
						${dealer.Phone}<br>
						<a href="mailto:${dealer.Email}" class="text-black underline">
							${dealer.Email}
						</a>
					</address>

					${shopInformationList.length > 0 ? `
						<div>
							<strong>${openingHourstext}</strong>
							${shopInformationList.map((row: ShopInformationRow) => `
								 <div class="flex gap-x-2">
									${row.columnOne ? `<div class="${row.columnTwo ? 'basis-1/2' : ''}">${row.columnOne}</div>` : ''}
									${row.columnTwo ? `<div>${row.columnTwo}</div>` : ''}
								</div>
							`).join('')}
						</div>
					` : ''}

					${dealer.ShopInformationText ? `
						<div>${dealer.ShopInformationText}</div>
					` : ''}

					<div class="flex">
						<a href="${dealer.Link}" class="cursor-pointer rounded-full hover:no-underline focus:no-underline rounded-full bg-primary text-white hover:bg-primary-dark active:border-primary-darker px-6 py-2 flex items-center justify-center gap-2 font-bold disabled:opacity-25 js-shop-link" data-dealer="${dealer.MenuText}">
							${viewShopText}
						</a>
					</div>
				</div>
				`

		const infoWindow = new InfoWindow({
			content: contentString
		})

		infoWindows.push(infoWindow)

		const latLng = { lat: dealer.Lat, lng: dealer.Lng }
		const image = document.createElement('img')
		image.width = 34
		image.height = 52
		image.decoding = 'sync'
		image.alt = dealer.ShopName ?? ''
		image.src = mapMarkerImgUrl

		const marker = new AdvancedMarkerElement({
			map,
			position: latLng,
			content: image
		})

		marker.addListener('click', function () {
			closeInfoWindows()
			if (map) {
				infoWindow.open(map, marker)
			}
		})

		markers.push(marker)
	})

	ensureBodyClickHandler()

	if (markersData.length > 1) {
		const bounds = new google.maps.LatLngBounds()
		markersData.forEach((dealer: ShopMarkerData) => {
			bounds.extend({ lat: dealer.Lat, lng: dealer.Lng })
		})
		map.fitBounds(bounds)
	} else if (markersData.length === 1) {
		map.setCenter({ lat: markersData[0].Lat, lng: markersData[0].Lng })
		map.setZoom(15)
	} else {
		map.setCenter(mapCenter)
		map.setZoom(7)
	}
}

const loadMap = async (token = requestToken) => {
	await loadGoogleMaps(mapsData.apiKey)

	if (token !== requestToken) {
		return
	}

	await renderMap()

	if (token !== requestToken) {
		return
	}

	mapLoaded.value = true
}

const getDealers = async (country: string) => {
	try {
		const response = await fetch(buildEndpoint(country))
		if (!response.ok) {
			console.error('Network response was not ok')
			return { shops: [] }
		}

		return await response.json()
	} catch (error) {
		console.error('There has been a problem with your fetch operation:', error)
		return { shops: [] }
	}
}

const loadDealersForCountry = async (country: string) => {
	const token = ++requestToken
	const data = await getDealers(country)
	if (token !== requestToken) {
		return
	}

	dealers.value = data?.shops ?? []

	if (hasCookieConsent()) {
		await loadMap(token)
	}
}

const loadAndSetCountries = async () => {
	try {
		const response = await fetch('/dwapi/ecommerce/international/countries')
		if (!response.ok) {
			console.error('Failed to load countries')
			return
		}

		const json: CountryApiItem[] = await response.json()
		const options = json
			.filter((country) => country.Code2 && country.Name)
			.map((country) => ({
				id: country.Code2 as string,
				name: country.Name as string,
				value: country.Code2 as string,
			}))

		countryOptions.value = options

		const preferredCountry = options.find((country) => country.value === selectedCountry.value) ?? options[0]
		if (preferredCountry) {
			selectedCountry.value = preferredCountry.value
		}
	} catch (error) {
		console.error('There has been a problem loading countries:', error)
	}
}

const handleCookiebotAccept = () => {
	if (hasCookieConsent()) {
		void loadMap()
	}
}

onMounted( () => {
	void loadAndSetCountries()

	if (!hasCookieConsent()) {
		window.addEventListener('CookiebotOnAccept', handleCookiebotAccept, false)
	}
})

onUnmounted(() => {
	document.body.removeEventListener('click', handleBodyClick)
	window.removeEventListener('CookiebotOnAccept', handleCookiebotAccept)
})

watch(selectedCountry, (country) => {
	if (country) {
		void loadDealersForCountry(country)
	}
})

const RenderAction = defineAsyncComponent(() => import('@/components/helpers/RenderAction.vue'))
const renewCookies = () => {
	Cookiebot.renew()
}


</script>

<template>
	<div id="maps">
		<div class="container mb-8">
			<div class="my-4 flex items-center justify-end gap-3">
				<SelectElement
					:options="countryOptions"
					:pre-selected-options="selectedCountryOptions"
					:label="getTranslation('FindShopCountryLabel', 'Land')"
					@updateFilter="handleCountryChange"
				>
					{{ selectedCountryOptions[0]?.name ?? getTranslation('FindShopCountryLabel', 'Land') }}
				</SelectElement>
			</div>
			<div
				ref="mapContainer"
				class="block min-h-[40rem] all-shops cookieconsent-optin-statistics cookieconsent-optin-marketing touch-none">
			</div>
			<div
				v-if="!mapLoaded"
				class="flex flex-col mt-3 mb-5 p-5 bg-warning rounded cookieconsent-optout-statistics cookieconsent-optout-marketing">
				<div class="p-2 text-center">{{ getTranslation('BlockedContentAllowCookiesText') }}</div>
				<div class="p-2 text-center">
					<a class="md:ml-auto px-6 py-2 rounded-full bg-black text-white hover:bg-black/90" href="javascript:Cookiebot.renew()">{{ getTranslation('ChangeCookieSettingsButtonText') }}</a>
				</div>
				<RenderAction
					action="button"
					theme="dark"
					size="sm"
					@click="renewCookies">
					{{ getTranslation('ChangeCookieSettingsButtonText') }}
				</RenderAction>
			</div>
		</div>

		<div class="bg-grey-light py-8">
			<div class="container">
				<div class="grid sm:grid-cols-1 md:grid-cols-4 gap-4">
					<DealerCard
						v-for="dealer in dealers"
						:key="dealer.ShopName"
						:dealer="dealer"
					/>
				</div>
			</div>
		</div>
	</div>
</template>
